import { useEffect, useRef, useState } from 'react';

import { ColorName, List } from 'types';

import { ButtonMotives, ButtonProps } from 'components/Button';

import { useDropdown } from './Dropdown.hooks';
import * as S from './Dropdown.styles';
import { ListItemProps, ListProps } from './Dropdown.styles';

export type DropdownProps = {
  items: List[];
  emptyMessage?: string;
  label: string;
  labelMotive?: ButtonMotives;
  onScrollEnd?: () => void;
  triggerOnClick?: boolean;
  alignLeft?: boolean;
  wideTrigger?: boolean;
  triggerMinWidth?: string;
} & ListProps &
  ListItemProps &
  Partial<ButtonProps>;

const defaultProps: Partial<DropdownProps> = {
  motive: 'default',
};

export function Dropdown(props: DropdownProps): React.ReactElement {
  const {
    items,
    emptyMessage,
    motive,
    itemMotive,
    labelMotive,
    label,
    hasAnimation,
    itemsHasBorder,
    iconLeft,
    iconRight,
    scale,
    fontWeight,
    settingsBarItem,
    onScrollEnd,
    triggerOnClick,
    alignLeft,
    wideTrigger,
    triggerMinWidth,
  } = {
    ...defaultProps,
    ...props,
  };

  const { listMapped, isOpen, isOpenSet, isHover, barColor } = useDropdown(
    items,
    itemMotive,
    hasAnimation,
    itemsHasBorder,
    alignLeft
  );

  const scrollEndFiredRef = useRef(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Let the mouse wheel scroll this row horizontally (native listener so
  // preventDefault works — React binds onWheel as passive at the root).
  useEffect(() => {
    if (motive !== 'default') return undefined;

    const el = scrollContainerRef.current;
    if (!el) return undefined;

    const onWheel = (e: WheelEvent) => {
      if (el.scrollWidth <= el.clientWidth) return;

      // A trackpad's left/right swipe already reports a real deltaX — let the
      // browser's native horizontal scroll (overflow-x: auto) handle that
      // instead of overriding it with our deltaY conversion below.
      if (e.deltaX !== 0) return;

      // When the menu also overflows vertically, let a plain wheel/swipe
      // scroll it up/down natively; only hijack for horizontal scrolling when
      // there's nothing to scroll vertically, or the user holds Shift (the
      // conventional "scroll horizontally" modifier).
      if (el.scrollHeight > el.clientHeight && !e.shiftKey) return;

      // Mouse wheels have no horizontal axis of their own — convert their
      // vertical motion into horizontal scroll.
      e.preventDefault();
      el.scrollLeft += e.deltaY;
    };

    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [motive]);

  // Close dropdown on outside click when triggerOnClick is enabled
  useEffect(() => {
    if (!triggerOnClick || !isOpen) return undefined;

    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        isOpenSet(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [triggerOnClick, isOpen]);

  // Reset scrollEndFired when dropdown opens
  useEffect(() => {
    if (isOpen) {
      scrollEndFiredRef.current = false;
    }
  }, [isOpen]);

  // Reset scrollEndFired when new items are appended
  useEffect(() => {
    scrollEndFiredRef.current = false;
  }, [items.length]);

  // Custom vertical scrollbar for motive="settings" (e.g. the ZONE dropdown):
  // a real, native scrollbar-thumb drag is handled by the OS/browser, which
  // ignores our CSS cursor once the drag starts, so the "grabbing" hand
  // snaps back to a plain arrow mid-drag. Dragging our own thumb element
  // instead is a normal mousemove/mouseup sequence we fully control, so the
  // cursor stays correct for the whole gesture.
  const [thumb, thumbSet] = useState<{ height: number; top: number } | null>(null);

  const updateThumb = () => {
    const el = scrollContainerRef.current;
    if (!el || el.scrollHeight <= el.clientHeight) {
      thumbSet(null);
      return;
    }

    thumbSet({
      height: (el.clientHeight / el.scrollHeight) * el.clientHeight,
      top: (el.scrollTop / el.scrollHeight) * el.clientHeight,
    });
  };

  // This dropdown opens on hover via CSS (see `mediaHover` below), so
  // `isOpen` (only ever set by a click) never actually flips for it — the
  // thumb has to be measured as soon as the content is in the DOM, not on
  // some "now it's open" state change. ResizeObserver's first callback
  // fires as soon as we start observing, so this also covers the initial
  // measurement without waiting for a scroll event to trigger it.
  useEffect(() => {
    if (motive !== 'settings') return undefined;

    const el = scrollContainerRef.current;
    if (!el) return undefined;

    const resizeObserver = new ResizeObserver(() => updateThumb());
    resizeObserver.observe(el);

    return () => resizeObserver.disconnect();
  }, [motive, items.length]);

  const handleThumbMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    const el = scrollContainerRef.current;
    if (!el || el.scrollHeight <= el.clientHeight) return;

    const startY = e.clientY;
    const startScrollTop = el.scrollTop;
    const scrollRatio = el.scrollHeight / el.clientHeight;

    document.body.classList.add('is-dragging-scrollbar');

    const onMouseMove = (moveEvent: MouseEvent) => {
      el.scrollTop = startScrollTop + (moveEvent.clientY - startY) * scrollRatio;
    };

    const onMouseUp = () => {
      document.body.classList.remove('is-dragging-scrollbar');
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    updateThumb();

    if (!onScrollEnd) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 50;

    if (isNearBottom && !scrollEndFiredRef.current) {
      scrollEndFiredRef.current = true;
      onScrollEnd();
    } else if (!isNearBottom) {
      // Reset once user scrolls back up — allows firing again on next scroll-to-bottom
      scrollEndFiredRef.current = false;
    }
  };

  const listContent =
    items.length > 0 ? (
      <S.FlexContainer $compact={itemMotive === 'settings'} $alignLeft={alignLeft}>
        {listMapped}
      </S.FlexContainer>
    ) : (
      <S.EmptyMessage
        fontFamily="LFT Zeno"
        fontWeight="extraLight"
        scale="xs"
        textTransform="uppercase"
        textAlign="center"
      >
        {emptyMessage}
      </S.EmptyMessage>
    );

  return (
    <S.Dropdown triggerOnClick={triggerOnClick} ref={dropdownRef}>
      {label && (
        <S.Trigger
          data-testid="trigger"
          onClick={() => (triggerOnClick || !isHover ? isOpenSet(!isOpen) : null)}
          iconLeft={iconLeft}
          iconRight={iconRight}
          fontWeight={fontWeight}
          fontFamily="LFT Zeno"
          motive={labelMotive || motive}
          scale={scale}
          barColor={settingsBarItem ? (barColor as ColorName) : undefined}
          $wide={wideTrigger}
          $minWidth={triggerMinWidth}
          truncate={wideTrigger}
        >
          {label}
        </S.Trigger>
      )}

      {items.length > 0 || emptyMessage ? (
        <S.DropdownContainer
          ref={motive === 'default' ? scrollContainerRef : undefined}
          barColor={barColor as ColorName}
          motive={motive}
          isOpen={isOpen}
          onScroll={motive === 'default' ? handleScroll : undefined}
        >
          {motive === 'settings' ? (
            <>
              <S.ScrollArea ref={scrollContainerRef} onScroll={handleScroll}>
                {listContent}
              </S.ScrollArea>

              {thumb && (
                <S.ScrollTrack>
                  <S.ScrollThumb
                    style={{ height: `${thumb.height}px`, top: `${thumb.top}px` }}
                    onMouseDown={handleThumbMouseDown}
                  />
                </S.ScrollTrack>
              )}
            </>
          ) : (
            listContent
          )}
        </S.DropdownContainer>
      ) : null}
    </S.Dropdown>
  );
}
